<template>
    <div class="plan-builder flow__container-fixed flow__panel--positioned-buttons">
        <div>
            <section class="plan-builder__top">
                <h2 class="plan-builder__top__title">
                    Plan Builder
                </h2>
                <property-info class="plan-builder__top__property-info"
                               :property="property"
                               pre-text="for"
                               :show-change-link="true" />
            </section>
            <data-container class="plan-builder__data">
                <data-item label="Total" :value="totalCost" />
            </data-container>
            <flyout-wrapped-content def="wrappedFilters"
                                    bar-def="filterControlBar"
                                    class="plan-builder__filters"
                                    modifier="--filters"
                                    :bar-component="filterBarComponent"
                                    :show="showFilters"
                                    @toggle="onFilterToggle">
                <improvements-filters />
            </flyout-wrapped-content>
            <div class="plan-builder__main flow__container-fixed__panel">
                <aside class="plan-builder__lhs-column">
                    <category-filters class="plan-builder__category-filters"
                                      prefix="wide" />
                </aside>
                <section>
                    <filter-results class="plan-builder__filter-results" />
                    <improvements-grid v-if="showGrid"
                                       class="plan-builder__main__improvements-grid"
                                       :loading="loading" />
                    <improvements-data v-else
                                       class="plan-builder__main__improvements-grid" />
                </section>
            </div>
        </div>
        <aside>
            <div class="plan-builder__aside" :class="{ '--open': showMetrics }">
                <flyout-wrapped-content def="wrappedMetrics"
                                        bar-def="metricsControlBar"
                                        class="plan-builder__aside__metrics"
                                        modifier="--metrics"
                                        :bar-component="metricsBarComponent"
                                        :show="showMetrics"
                                        @toggle="onMetricsToggle">
                    <metrics-display />
                </flyout-wrapped-content>
                <basket v-if="!loading"
                        class="plan-builder__aside__basket"
                        display-type="icon"
                        editable
                        contained />
                <section class="plan-builder__cta">
                    <cta-tile name="review"
                              class="plan-builder__cta__tile"
                              :button-text="commonResources.continue"
                              :secondary-button-text="calculationsCtaText"
                              breakout-extended
                              :disabled="!canProgress"
                              @click="onProceed"
                              @secondary-click="onShowLegalText" />
                </section>
                <our-calculations class="plan-builder__legal-text" />
            </div>
        </aside>
        <flyout-mask class="plan-builder__mask --filters-mask"
                     :show="showFilters"
                     @click="onFilterToggle"
                     @keyup.esc="onFilterToggle" />
        <flyout-mask class="plan-builder__mask --metrics-mask"
                     :show="showMetrics"
                     @click="onMetricsToggle"
                     @keyup.esc="onMetricsToggle" />
    </div>
</template>

<script>

import AnalyticsPage from './analytics-page'
import MyHomeDataErrorsDisplay from './my-home-data-errors-display'
import PlanLoader from './plan-loader'
import ProgressManager from '@/progress-manager'

import Basket from '@/components/app-core/plan-basket'
import BreakpointTracker from '@/components/breakpoint-tracker'
import CopyPresenter from '@/components/app-core/modals/copy-presenter'
import CtaTile from '@/components/app-core/cta-tile'
import CategoryFilters from '@/components/plan-builder/category-filters'
import DataContainer from '@/components/app-core/data-container'
import FilterBar from '@/components/plan-builder/filter-bar'
import FlyoutMask from '@/components/plan-builder/flyout-mask'
import FilterResults from '@/components/plan-builder/filter-results'
import FlyoutWrappedContent from '@/components/plan-builder/flyout-wrapped-content'
import ImprovementsFilters from '@/components/plan-builder/improvements-filters'
import ImprovementsData from '@/components/plan-builder/improvements-data'
import ImprovementsGrid from '@/components/plan-builder/improvements-grid'
import MetricsBar from '@/components/plan-builder/metrics-bar'
import MetricsDisplay from '@/components/app-core/metrics-display'
import OurCalculations from '@/components/copy/our-calculations'
import PropertyInfo from '@/components/app-core/property-info'
import ResourcesHelper from '@/components/resources-helper'

export default {

    mixins: [
        AnalyticsPage,
        BreakpointTracker,
        MyHomeDataErrorsDisplay,
        PlanLoader,
        ResourcesHelper,
    ],

    data() {
        return {
            /**
             * @type {string}
             */
            name: 'Plan Builder',

            /**
             * @type {boolean}
             */
            showFilters: false,

            /**
             * @type {boolean}
             */
            showMetrics: false,

            /**
             * Set this value as false to show the data table - useful for debugging results.
             * @type {boolean}
             */
            showGrid: true,
        }
    },

    computed: {
        /**
         * @returns {PlanBuilderResources}
         */
        myResources() {
            return this.parseResources('views.planBuilder')
        },

        /**
         * @returns {boolean}
         */
        canProgress() {
            return !this.loading && this.currentProject.addedImprovements.length > 0
        },

        /**
         * @returns {Vue|Object}
         */
        filterBarComponent() {
            return FilterBar
        },

        /**
         * @returns {Vue|Object}
         */
        metricsBarComponent() {
            return MetricsBar
        },

        /**
         * @returns {string}
         */
        totalCost() {
            return this.currentProject.costDisplay
        },

        /**
         * @returns {?string}
         */
        calculationsCtaText() {
            return this.activeBreakpoint === 'small' ? this.myResources.calculationsCta : null
        },
    },

    mounted() {
        this.trackEvent('plan_build', 'plan_build_started')
    },

    beforeDestroy() {
        this.trackEvent('plan_build', 'plan_build_stopped')
    },

    methods: {
        /**
         * Toggle state of showFilters boolean property.
         */
        onFilterToggle() {
            this.showFilters = !this.showFilters
        },

        /**
         * Toggle state of showMetrics boolean property.
         */
        onMetricsToggle() {
            this.showMetrics = !this.showMetrics
        },

        /**
         * Move to the next screen.
         */
        onProceed() {
            this.$router.push(ProgressManager.getNext(this.$route.name))
        },

        /**
         * Show the legal text for a mobile viewport.
         */
        onShowLegalText() {
            const modalData = {
                data: { title: this.allResources.components.copy.ourCalculations.title },
                cssModifier: 'our-calculations',
                nestedComponent: OurCalculations,
            }

            this.$eventbus.$emit(this.$appevents.showModal, CopyPresenter, modalData)
        },
    },

    components: {
        Basket,
        CtaTile,
        CategoryFilters,
        DataContainer,
        DataItem: DataContainer.Item,
        FilterResults,
        FlyoutMask,
        FlyoutWrappedContent,
        ImprovementsFilters,
        ImprovementsData,
        ImprovementsGrid,
        MetricsDisplay,
        OurCalculations,
        PropertyInfo,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_plan-builder.scss

.plan-builder {
    @include layout-primary();

    @include element('top') {
        @include element('title') {
            min-height: 2.5rem;

            @include bp-min-mid {
                min-height: auto;
            }

            @include bp-max-small {
                font-size: 1.3rem;
            }
        }
    }

    @include element('main') {
      clear: both;
      padding-top: $base-gutter;
    }

    @include element('aside') {
        // needs z-index as the absolute positioning creates its own stacking context
        &.--open {
          z-index: z-index('dd');
        }

        @include bp-min-mid {
            position: sticky;
            top: 100px;
            right: 0;
        }

        @include element('metrics') {
            position: absolute;
            right: $base-gutter;
            top: 54px;
            width: calc(50vw - $base-gutter * 2);
            max-width: 250px;

            @include bp-min-mid {
                position: static;
                padding-left: 0;
                width: auto;
            }
        }

        @include element('basket') {
            display: none;
            position: relative;

            @include bp-min-mid {
                display: block;
                margin-top: 1rem;
            }
        }
    }

    @include element('lhs-column') {
        display: none;

        @include bp-min-mid {
            display: block;
        }
    }

    @include element('category-filters') {
        @include bp-min-mid {
            position: sticky;
            top: 100px;
        }
    }

    @include element('filter-results') {
        margin-left: $base-gutter;
    }

    @include element('filters') {
        @include main-page-lhs-all;
    }

    @include element('legal-text') {
        display: none;

        @include bp-min-mid {
            display: block;
        }
    }
}

</style>
